import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ApartmentOutlined,
  DollarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  PlusOutlined,
  SafetyOutlined,
  StarOutlined,
  WifiOutlined,
  CarOutlined,
  CoffeeOutlined,
  DribbbleOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import moment from "moment";
import api, { getImageUrl } from "../../api/client";
import apiPath from "../../constants/apiPath";
import { addRooms, updateRoom, deleteRoom, toggleRoomStatus, setQRPassword, resendAllocationEmail } from "../../api/rooms";
import { QrcodeOutlined, MailOutlined, LockOutlined, EyeOutlined } from "@ant-design/icons";
const WebsiteURL = apiPath.WEBSITE_URL;

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;
const { TabPane } = Tabs;
const { Option } = Select;

const getManagerId = () => {
  try {
    const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
    const loginData = JSON.parse(localStorage.getItem("loginData") || "{}");
    return profile?.Hotel_Id || loginData?.managerDetails?.Hotel_Id || null;
  } catch (_err) {
    return null;
  }
};

const HotelDetails = () => {
  const screens = useBreakpoint();

  const [loading, setLoading] = useState(true);
  const [hotelDetails, setHotelDetails] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const [addRoomModalVisible, setAddRoomModalVisible] = useState(false);
  const [addRoomLoading, setAddRoomLoading] = useState(false);
  const [form] = Form.useForm();

  const [editRoomModalVisible, setEditRoomModalVisible] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editRoomLoading, setEditRoomLoading] = useState(false);
  const [editForm] = Form.useForm();

  const [selectedAirlineId, setSelectedAirlineId] = useState(null);
  const [airlineOptions, setAirlineOptions] = useState([]);

  const managerId = useMemo(() => getManagerId(), []);

  const authRole = useMemo(() => localStorage.getItem("authRole") || "", []);

  const isAuthorized = useMemo(() => {
    const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
    const uType = profile?.userType || profile?.user_type || profile?.role || "";
    return uType === "HotelPartner" || uType === "Super_Admin" || uType === "admin" || authRole === "admin" || authRole === "hotel";
  }, [authRole]);

  const [meals, setMeals] = useState([]);
  const [mealsLoading, setMealsLoading] = useState(false);
  const [mealModalVisible, setMealModalVisible] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  const [mealSaveLoading, setMealSaveLoading] = useState(false);
  const [mealForm] = Form.useForm();

  // QR Allocation State
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm] = Form.useForm();

  const fetchMeals = useCallback(async () => {
    if (!hotelDetails?.Hotel_Id) return;
    setMealsLoading(true);
    try {
      const res = await api.get(`${apiPath.getMeals}/${hotelDetails.Hotel_Id}`);
      const data = res?.data;
      const mealsList = data?.meals || data?.data || data?.Details || (Array.isArray(data) ? data : []);
      setMeals(mealsList);
    } catch (err) {
      console.error("Error fetching meals:", err);
      message.error(err?.response?.data?.message || "Failed to fetch meals");
    } finally {
      setMealsLoading(false);
    }
  }, [hotelDetails?.Hotel_Id]);

  useEffect(() => {
    if (activeTab === "meals" && isAuthorized && hotelDetails?.Hotel_Id) {
      fetchMeals();
    }
  }, [activeTab, isAuthorized, hotelDetails?.Hotel_Id, fetchMeals]);

  const handleEditMealClick = (meal) => {
    setEditingMeal(meal);
    mealForm.setFieldsValue({
      meal_name: meal.meal_name,
      available: meal.available !== false,
    });
    setMealModalVisible(true);
  };

  const handleSaveMeal = async (values) => {
    setMealSaveLoading(true);
    try {
      const payload = {
        meal_name: values.meal_name,
        available: values.available !== false,
      };

      if (editingMeal) {
        await api.put(`${apiPath.updateMeal}/${hotelDetails.Hotel_Id}/${editingMeal._id || editingMeal.id || editingMeal.meal_id}`, payload);
        message.success("Meal updated successfully!");
      } else {
        await api.post(`${apiPath.addMeal}/${hotelDetails.Hotel_Id}`, payload);
        message.success("Meal added successfully!");
      }

      setMealModalVisible(false);
      setEditingMeal(null);
      mealForm.resetFields();
      fetchMeals();
    } catch (err) {
      console.error("Error saving meal:", err);
      message.error(err?.response?.data?.message || "Failed to save meal");
    } finally {
      setMealSaveLoading(false);
    }
  };

  const handleDeleteMeal = (meal) => {
    Modal.confirm({
      title: "Delete Meal",
      content: `Are you sure you want to delete "${meal.meal_name}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await api.delete(`${apiPath.deleteMeal}/${hotelDetails.Hotel_Id}/${meal._id || meal.id || meal.meal_id}`);
          message.success("Meal deleted successfully!");
          fetchMeals();
        } catch (err) {
          console.error("Error deleting meal:", err);
          message.error(err?.response?.data?.message || "Failed to delete meal");
        }
      },
    });
  };

  const fetchHotelDetails = useCallback(async () => {
    try {
      if (!managerId) {
        message.error("Manager session not found. Please login again.");
        setHotelDetails(null);
        return;
      }

      const res = await api.get(`get_Hotels_of_HotelPartner/${managerId}`);
      const payload = res?.data;
      const list =
        payload?.Hotels_details ||
        payload?.Hotel_Details ||
        payload?.hotel_Details ||
        payload?.data ||
        [];

      const hotelData = Array.isArray(list) ? list[0] : null;
      setHotelDetails(hotelData || null);

      if (hotelData) {
        const initialAirlineIds = hotelData.airline_ids && hotelData.airline_ids.length > 0
          ? hotelData.airline_ids
          : (hotelData.airline_id ? [hotelData.airline_id] : []);
        
        const defaultAirlineId = initialAirlineIds[0] || null;
        setSelectedAirlineId(defaultAirlineId);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching hotel details:", error);
      message.error(error?.response?.data?.message || "Failed to fetch hotel details");
    } finally {
      setLoading(false);
    }
  }, [managerId]);

  useEffect(() => {
    fetchHotelDetails();
  }, [fetchHotelDetails]);

  useEffect(() => {
    api.get("/airline/users")
      .then((res) => {
        const payload = res?.data;
        if (payload?.success && Array.isArray(payload.users)) {
          const activeUsers = payload.users.filter((u) => u.status === 1);
          setAirlineOptions(activeUsers.map((u) => ({ value: u._id || u.id, label: u.name })));
        }
      })
      .catch((err) => {
        console.error("Error fetching airline users:", err);
      });
  }, []);

  const getAssociatedAirlineOptions = () => {
    if (!hotelDetails) return [];
    const associatedIds = hotelDetails.airline_ids && hotelDetails.airline_ids.length > 0
      ? hotelDetails.airline_ids
      : (hotelDetails.airline_id ? [hotelDetails.airline_id] : []);
    
    return associatedIds.map(id => {
      const option = airlineOptions.find(o => o.value === id);
      return {
        value: id,
        label: option ? option.label : id
      };
    });
  };

  if (loading) return <Skeleton active style={{ margin: 40 }} />;
  if (!hotelDetails) return <Empty description="Hotel not found" style={{ padding: 24 }} />;

  const currentRooms = (hotelDetails?.rooms || []).filter(room => {
    if (!selectedAirlineId) return true;
    if (room.airline_id) {
      return room.airline_id.toString() === selectedAirlineId.toString();
    }
    return hotelDetails.airline_id && hotelDetails.airline_id.toString() === selectedAirlineId.toString();
  });

  const totalRooms = activeTab === "rooms"
    ? (currentRooms.reduce((t, r) => t + (r.Total_rooms || 0), 0) || 0)
    : (hotelDetails?.rooms?.reduce((t, r) => t + (r.Total_rooms || 0), 0) || 0);

  const totalTypes = activeTab === "rooms"
    ? currentRooms.length
    : (hotelDetails?.rooms?.length || 0);

  const getPriceRange = () => {
    let minPrice = Infinity;
    let maxPrice = -Infinity;
    hotelDetails?.rooms?.forEach((room) => {
      if (room.price < minPrice) minPrice = room.price;
      if (room.price > maxPrice) maxPrice = room.price;
    });
    return {
      min: minPrice === Infinity ? 0 : minPrice,
      max: maxPrice === -Infinity ? 0 : maxPrice,
    };
  };
  const priceRange = getPriceRange();

  const handleAddRoom = async (values) => {
    setAddRoomLoading(true);
    try {
      const hotelId = hotelDetails.Hotel_Id;
      const roomData = {
        type: values.type,
        Total_rooms: values.Total_rooms,
        airline_id: selectedAirlineId,
      };

      await addRooms(hotelId, roomData);
      message.success(`Room type "${values.type}" added successfully!`);
      setAddRoomModalVisible(false);
      form.resetFields();
      await fetchHotelDetails();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to add room type. Please try again.");
    } finally {
      setAddRoomLoading(false);
    }
  };



  const handleEditRoom = (room) => {
    setEditingRoom(room);
    editForm.setFieldsValue({
      room_number: room.room_number,
      type: room.type,
      price: room.price,
      capacity: room.capacity,
      description: room.description || "",
    });
    setEditRoomModalVisible(true);
  };

  const handleUpdateRoom = async (values) => {
    setEditRoomLoading(true);
    try {
      const hotelId = hotelDetails.Hotel_Id;
      const roomType = editingRoom.type;
      const roomData = {
        Total_rooms: values.Total_rooms,
        status: values.status,
        airline_id: selectedAirlineId,
      };
      await updateRoom(hotelId, roomType, roomData);
      message.success("Room type updated successfully!");
      setEditRoomModalVisible(false);
      setEditingRoom(null);
      editForm.resetFields();
      await fetchHotelDetails();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to update room type. Please try again.");
    } finally {
      setEditRoomLoading(false);
    }
  };

  const handleDeleteRoom = async (room) => {
    Modal.confirm({
      title: "Delete Room Type",
      content: `Are you sure you want to delete room type "${room.type}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteRoom(hotelDetails.Hotel_Id, room.type, selectedAirlineId);
          message.success("Room type deleted successfully!");
          await fetchHotelDetails();
        } catch (error) {
          message.error(error?.response?.data?.message || "Failed to delete room type. Please try again.");
        }
      },
    });
  };

  const handleToggleRoomStatus = async (room, status) => {
    try {
      await toggleRoomStatus(hotelDetails.Hotel_Id, room.type, status ? 1 : 0, selectedAirlineId);
      message.success("Room status updated successfully!");
      await fetchHotelDetails();
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to update room status. Please try again."
      );
    }
  };

  const handleViewQR = () => {
    const matchingRooms = (hotelDetails.rooms || []).filter(r => 
      !selectedAirlineId || 
      String(r.airline_id) === String(selectedAirlineId) ||
      (!r.airline_id && String(hotelDetails.airline_id) === String(selectedAirlineId))
    );
    const token = matchingRooms[0]?.qr_token || hotelDetails.qr_token || (hotelDetails.rooms || []).find(r => r.qr_token)?.qr_token;
    const password = matchingRooms[0]?.qr_password || hotelDetails.qr_password || (hotelDetails.rooms || []).find(r => r.qr_password)?.qr_password;
    const roomsCount = matchingRooms.reduce((acc, r) => acc + (r.available_rooms || 0), 0);

    setSelectedAllocation({
      type: "All Allocated Types",
      qr_token: token,
      qr_password: password,
      available_rooms: roomsCount,
      status: (hotelDetails.status === 1 || hotelDetails.status === "active" || hotelDetails.status === true) ? 1 : 0
    });
    setQrModalVisible(true);
  };

  const handleResendEmail = async () => {
    try {
      await resendAllocationEmail(hotelDetails.Hotel_Id, undefined, selectedAirlineId);
      message.success("Allocation email resent successfully");
    } catch (error) {
      message.error("Failed to resend email");
    }
  };

  const handleSetPassword = async (values) => {
    try {
      setPasswordLoading(true);
      await setQRPassword(hotelDetails.Hotel_Id, undefined, values.password, selectedAirlineId);
      message.success("Password updated successfully");
      setPasswordModalVisible(false);
      passwordForm.resetFields();
      fetchHotelDetails();
    } catch (error) {
      message.error("Failed to update password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const getFacilityIcon = (facility) => {
    const facilityLower = facility.toLowerCase();
    if (facilityLower.includes("wifi") || facilityLower.includes("internet"))
      return <WifiOutlined />;
    if (facilityLower.includes("parking") || facilityLower.includes("car")) return <CarOutlined />;
    if (
      facilityLower.includes("restaurant") ||
      facilityLower.includes("food") ||
      facilityLower.includes("coffee")
    )
      return <CoffeeOutlined />;
    if (facilityLower.includes("pool") || facilityLower.includes("swimming"))
      return <DribbbleOutlined />;
    if (facilityLower.includes("gym") || facilityLower.includes("fitness")) return <TeamOutlined />;
    return <HomeOutlined />;
  };

  return (
    <Card
    >
      <div style={{ marginBottom: 32 }}>
        <div
          style={{
            position: "relative",
            borderRadius: 16,
            overflow: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
          }}
        >
          <img
            src={getImageUrl(hotelDetails.HotelImages?.[0])}
            alt={hotelDetails.Hotel_name}
            style={{
              width: "100%",
              height: screens.xs ? 260 : 450,
              objectFit: "cover",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent)",
              padding: screens.xs ? "28px 18px 18px" : "50px 40px 30px",
            }}
          >
            <Title
              level={screens.xs ? 3 : 1}
              style={{
                color: "#fff",
                margin: 0,
                fontWeight: 700,
                letterSpacing: "-0.5px",
              }}
            >
              {hotelDetails.Hotel_name}
            </Title>

            <Space style={{ marginTop: 16 }} size="middle" wrap>
              {/* <Tag color="gold" style={{ fontSize: 14, padding: "4px 16px", borderRadius: 20 }}>
                <StarOutlined /> {hotelDetails.starRating || 4} Star Hotel
              </Tag> */}
              <Tag color="blue" style={{ fontSize: 14, padding: "4px 16px", borderRadius: 20 }}>
                {hotelDetails.hotelType || "Hotel"}
              </Tag>
              <Tag
                color={hotelDetails.status ? "green" : "red"}
                style={{ fontSize: 14, padding: "4px 16px", borderRadius: 20 }}
              >
                {hotelDetails.status ? "Active" : "Inactive"}
              </Tag>

            </Space>
          </div>
        </div>
      </div>

      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Statistic
              title={<Text type="secondary">Room Types</Text>}
              value={totalTypes}
              prefix={<ApartmentOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ fontSize: 32, fontWeight: 600, color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Statistic
              title={<Text type="secondary">Total Rooms</Text>}
              value={totalRooms}
              prefix={<HomeOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ fontSize: 32, fontWeight: 600, color: "#52c41a" }}
            />
          </Card>
        </Col>
        {/* <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Statistic
              title={<Text type="secondary">Commission Rate</Text>}
              value={String(hotelDetails.commision_rate ?? 15).replace(/%/g, "")}
              suffix="%"
              valueStyle={{ fontSize: 32, fontWeight: 600, color: "#faad14" }}
            />
          </Card>
        </Col> */}
        {/* <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Statistic
              title={<Text type="secondary">Price Range</Text>}
              value={`${priceRange.min.toLocaleString()}`}
              suffix={`-${priceRange.max.toLocaleString()}`}
              prefix={<DollarOutlined style={{ color: "#eb2f96" }} />}
              valueStyle={{ fontSize: 24, fontWeight: 600, color: "#eb2f96" }}
            />
          </Card>
        </Col> */}
      </Row>

      <Card
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" type="card">
          <TabPane
            tab={
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
                <InfoCircleOutlined style={{ color: "#1890ff" }} />
                <span>Overview</span>
              </span>
            }
            key="overview"
          >
            <Row gutter={[32, 32]}>
              <Col xs={24} lg={14}>
                <Card
                  title={<Title level={4} style={{ margin: 0 }}>About Hotel</Title>}
                  bordered={false}
                  style={{ background: "#fafafa", borderRadius: 12 }}
                >
                  <Paragraph
                    style={{
                      fontSize: 15,
                      lineHeight: 1.8,
                      color: "#4a4a4a",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      background: "#fff",
                      padding: "16px 20px",
                      borderRadius: 8,
                      borderLeft: "4px solid #1890ff",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                    }}
                    ellipsis={{ rows: 8, expandable: true, symbol: "Read more" }}
                  >
                    {hotelDetails.aboutHotel || "No description provided for this hotel."}
                  </Paragraph>
                </Card>

                <Card
                  title={
                    <Space>
                      <EnvironmentOutlined style={{ color: "#ff4d4f" }} />
                      <span>Location Details</span>
                    </Space>
                  }
                  bordered={false}
                  style={{ marginTop: 24, background: "#fafafa", borderRadius: 12 }}
                >
                  <div style={{ background: "#fff", borderRadius: 8, padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                    <Row gutter={[24, 24]}>
                      <Col span={24}>
                        <Space align="start" size="middle">
                          <Avatar icon={<EnvironmentOutlined />} style={{ backgroundColor: "#fff1f0", color: "#ff4d4f" }} />
                          <div>
                            <Text type="secondary" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Full Address</Text>
                            <Text strong style={{ fontSize: 15, display: "block" }}>{hotelDetails.address || "N/A"}</Text>
                          </div>
                        </Space>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Space align="start" size="middle">
                          <Avatar icon={<ApartmentOutlined />} style={{ backgroundColor: "#e6f7ff", color: "#1890ff" }} />
                          <div>
                            <Text type="secondary" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>City</Text>
                            <Text strong style={{ fontSize: 15 }}>{hotelDetails.city || "N/A"}</Text>
                          </div>
                        </Space>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Space align="start" size="middle">
                          <Avatar icon={<GlobalOutlined />} style={{ backgroundColor: "#f9f0ff", color: "#722ed1" }} />
                          <div>
                            <Text type="secondary" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Country</Text>
                            <Text strong style={{ fontSize: 15 }}>{hotelDetails.country || "N/A"}</Text>
                          </div>
                        </Space>
                      </Col>
                    </Row>
                  </div>
                </Card>
              </Col>

              <Col xs={24} lg={10}>
                {/* <Card
                  title={
                    <Space>
                      <StarOutlined style={{ color: "#faad14" }} />
                      <span>Facilities & Amenities</span>
                    </Space>
                  }
                  bordered={false}
                  style={{ background: "#fafafa", borderRadius: 12 }}
                >
                  {hotelDetails.facilities?.length > 0 ? (
                    <Space wrap size={[8, 12]}>
                      {hotelDetails.facilities.map((facility, i) => (
                        <Tag
                          key={i}
                          icon={getFacilityIcon(facility)}
                          style={{
                            fontSize: 14,
                            padding: "6px 16px",
                            borderRadius: 20,
                            border: "none",
                            background: "#fff",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                          }}
                        >
                          {facility}
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">No facilities listed</Text>
                  )}
                </Card> */}

                {(hotelDetails.check_in_time || hotelDetails.check_out_time || hotelDetails.cancellation_policy) && (
                  <Card
                    title={
                      <Space>
                        <SafetyOutlined style={{ color: "#52c41a" }} />
                        <span>Policies</span>
                      </Space>
                    }
                    bordered={false}
                    style={{ marginTop: 24, background: "#fafafa", borderRadius: 12 }}
                  >
                    <div style={{ background: "#fff", borderRadius: 8, padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                      <Row gutter={[16, 24]}>
                        {hotelDetails.check_in_time && (
                          <Col span={24} md={12}>
                            <Space direction="vertical" size={4}>
                              <Space style={{ color: "#52c41a" }}>
                                <ClockCircleOutlined />
                                <Text strong style={{ color: "inherit" }}>Check-in</Text>
                              </Space>
                              <Text style={{ fontSize: 16, fontWeight: 500, marginLeft: 24 }}>
                                {hotelDetails.check_in_time.includes("GMT")
                                  ? moment(hotelDetails.check_in_time).format("hh:mm A")
                                  : hotelDetails.check_in_time}
                              </Text>
                            </Space>
                          </Col>
                        )}
                        {hotelDetails.check_out_time && (
                          <Col span={24} md={12}>
                            <Space direction="vertical" size={4}>
                              <Space style={{ color: "#ff4d4f" }}>
                                <ClockCircleOutlined />
                                <Text strong style={{ color: "inherit" }}>Check-out</Text>
                              </Space>
                              <Text style={{ fontSize: 16, fontWeight: 500, marginLeft: 24 }}>
                                {hotelDetails.check_out_time.includes("GMT")
                                  ? moment(hotelDetails.check_out_time).format("hh:mm A")
                                  : hotelDetails.check_out_time}
                              </Text>
                            </Space>
                          </Col>
                        )}
                        {hotelDetails.cancellation_policy && (
                          <Col span={24}>
                            <div style={{ padding: "12px", background: "#fffbe6", borderRadius: 8, borderLeft: "4px solid #faad14", marginTop: 8 }}>
                              <Text strong style={{ fontSize: 13, color: "#856404", display: "block", marginBottom: 4 }}>Cancellation Policy</Text>
                              <Paragraph style={{ margin: 0, fontSize: 14 }} type="secondary">
                                {hotelDetails.cancellation_policy}
                              </Paragraph>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </Card>
                )}
              </Col>
            </Row>
          </TabPane>

          <TabPane
            tab={
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
                <HomeOutlined style={{ color: "#52c41a" }} />
                <span>Rooms & Suites</span>
              </span>
            }
            key="rooms"
          >
            <div style={{ padding: "8px 0" }}>
              {getAssociatedAirlineOptions().length > 0 && (
                <div style={{
                  background: '#f8fafc',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 16
                }}>
                  <Text strong style={{ color: '#475569' }}>Selected Airline Context:</Text>
                  <Select
                    style={{ width: 280 }}
                    value={selectedAirlineId}
                    onChange={(val) => setSelectedAirlineId(val)}
                    options={getAssociatedAirlineOptions()}
                    placeholder="Select airline to manage allocation"
                  />
                </div>
              )}
              <Card
                style={{ marginBottom: 24, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", borderRadius: 12 }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <Row gutter={24} align="middle">
                  <Col>
                    <Avatar size={64} icon={<HomeOutlined />} style={{ backgroundColor: "rgba(255,255,255,0.2)" }} />
                  </Col>
                  <Col flex="auto">
                    <Title level={4} style={{ color: "#fff", margin: 0 }}>Room Inventory</Title>
                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                      Total {totalRooms} rooms • {totalTypes} room types available
                    </Text>
                  </Col>
                  <Col>
                    <Space size="middle">
                      <Button
                        type="default"
                        size="large"
                        icon={<QrcodeOutlined />}
                        style={{
                          background: "rgba(255,255,255,0.9)",
                          color: "#1890ff",
                          border: "none",
                          fontWeight: 600,
                          borderRadius: 8,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        }}
                        onClick={() => handleViewQR()}
                      >
                        View Hotel QR
                      </Button>
                      {authRole === "airline" && (
                        <Button
                          type="default"
                          size="large"
                          icon={<LockOutlined />}
                          style={{
                            background: "rgba(255,255,255,0.9)",
                            color: "#faad14",
                            border: "none",
                            fontWeight: 600,
                            borderRadius: 8,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          }}
                          onClick={() => {
                            const matchingRooms = (hotelDetails.rooms || []).filter(r => 
                              !selectedAirlineId || String(r.airline_id) === String(selectedAirlineId)
                            );
                            setSelectedAllocation({
                              type: "All Allocated Types",
                              qr_token: hotelDetails.qr_token || matchingRooms[0]?.qr_token || (hotelDetails.rooms && hotelDetails.rooms[0]?.qr_token),
                              qr_password: hotelDetails.qr_password || matchingRooms[0]?.qr_password || (hotelDetails.rooms && hotelDetails.rooms[0]?.qr_password),
                            });
                            setPasswordModalVisible(true);
                          }}
                        >
                          Set Password
                        </Button>
                      )}
                      {authRole !== "airline" && (
                        <Button
                          type="primary"
                          size="large"
                          icon={<PlusOutlined />}
                          style={{
                            background: "rgba(255,255,255,0.9)",
                            color: "#667eea",
                            border: "none",
                            fontWeight: 600,
                            borderRadius: 8,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          }}
                          onClick={() => setAddRoomModalVisible(true)}
                        >
                          Add Rooms
                        </Button>
                      )}
                    </Space>
                  </Col>
                </Row>
              </Card>



              {currentRooms.length > 0 ? (
                <Card
                  title={
                    <Space>
                      <HomeOutlined style={{ color: "#1890ff" }} />
                      <Text strong style={{ fontSize: 18 }}>Current Room Inventory</Text>
                      <Tag color="geekblue" style={{ borderRadius: 20 }}>{currentRooms.length} Types</Tag>
                    </Space>
                  }
                  style={{ borderRadius: 12, overflow: "hidden" }}
                  bordered={false}
                >
                  <Table
                    columns={[
                      { title: "Room Type", dataIndex: "type", key: "type", render: (type) => <Tag color="blue" style={{ borderRadius: 20, padding: "2px 12px", fontSize: 14 }}>{type}</Tag> },
                      { title: "Total Rooms", dataIndex: "Total_rooms", key: "Total_rooms", render: (count) => <Text strong style={{ fontSize: 16 }}>{count}</Text> },
                      { title: "Available", dataIndex: "available_rooms", key: "available_rooms", render: (count) => <Text strong style={{ color: "#52c41a", fontSize: 16 }}>{count ?? "-"}</Text> },
                      // { title: "Price", dataIndex: "price", key: "price", render: (price) => <Text strong style={{ color: "#132272" }}>${price?.toLocaleString() || "N/A"}</Text> },
                      { title: "Status", key: "status", render: (_, record) => (<Badge status={record.status === 1 ? "success" : "error"} text={record.status === 1 ? "Active" : "Inactive"} />) },
                      {
                        title: "Actions",
                        key: "actions",
                        render: (_, record) => (
                          <Space size="middle">
                            {authRole !== "airline" && (
                              <>
                                <Tooltip title="Edit Type">
                                  <Button icon={<EditOutlined />} style={{ color: "#1890ff", margin: "0px" }} onClick={() => handleEditRoom(record)} />
                                </Tooltip>
                                <Tooltip title="Delete Type">
                                  <Button danger style={{ color: "#1890ff", margin: "0px" }} onClick={() => handleDeleteRoom(record)}>
                                    <DeleteOutlined />
                                  </Button>
                                </Tooltip>
                                <Tooltip title={record.status === 1 ? "Deactivate" : "Activate"}>
                                  <Switch checked={record.status === 1} onChange={(checked) => handleToggleRoomStatus(record, checked)} size="small" checkedChildren="Active" unCheckedChildren="Inactive" />
                                </Tooltip>
                              </>
                            )}
                          </Space>
                        ),
                      },
                    ]}
                    dataSource={currentRooms}
                    rowKey={(record) => record.type}
                    pagination={false}
                    size="middle"
                  />
                </Card>
              ) : (
                <Empty description="No room types available" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "40px 0" }} />
              )}
            </div>
          </TabPane>

          {isAuthorized && (
            <TabPane
              tab={
                <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
                  <CoffeeOutlined style={{ color: "#fa8c16" }} />
                  <span>Meals & Dining</span>
                </span>
              }
              key="meals"
            >
              <div style={{ padding: "8px 0" }}>
                <Card
                  style={{ marginBottom: 24, background: "linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)", borderRadius: 12 }}
                  bodyStyle={{ padding: "20px 24px" }}
                >
                  <Row gutter={24} align="middle">
                    <Col>
                      <Avatar size={64} icon={<CoffeeOutlined />} style={{ backgroundColor: "rgba(255,255,255,0.2)" }} />
                    </Col>
                    <Col flex="auto">
                      <Title level={4} style={{ color: "#fff", margin: 0 }}>Meals Management</Title>
                      <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                        Manage dining options for your guests.
                      </Text>
                    </Col>
                    <Col>
                      <Space>
                        <Button
                          type="primary"
                          size="large"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setEditingMeal(null);
                            mealForm.resetFields();
                            mealForm.setFieldsValue({ available: true });
                            setMealModalVisible(true);
                          }}
                        >
                          Add Meal
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </Card>

                <Card
                  style={{ borderRadius: 12, overflow: "hidden" }}
                  bordered={false}
                >
                  <Table
                    loading={mealsLoading}
                    columns={[
                      {
                        title: "Meal Name",
                        dataIndex: "meal_name",
                        key: "meal_name",
                        render: (text) => <Text strong style={{ fontSize: 16 }}>{text}</Text>
                      },
                      {
                        title: "Availability",
                        dataIndex: "available",
                        key: "available",
                        render: (available) => (
                          <Badge
                            status={available !== false ? "success" : "error"}
                            text={available !== false ? "Available" : "Not Available"}
                          />
                        )
                      },
                      {
                        title: "Actions",
                        key: "actions",
                        render: (_, record) => (
                          <Space>
                            <Tooltip title="Edit Meal">
                              <Button
                                type="text"
                                icon={<EditOutlined />}
                                size="small"
                                style={{ color: "#1890ff" }}
                                onClick={() => handleEditMealClick(record)}
                              />
                            </Tooltip>
                            <Tooltip title="Delete Meal">
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={() => handleDeleteMeal(record)}
                              />
                            </Tooltip>
                          </Space>
                        )
                      }
                    ]}
                    dataSource={meals}
                    rowKey={(record) => record._id || record.id || record.meal_id || Math.random().toString()}
                    pagination={{ pageSize: 10 }}
                    size="middle"
                  />
                </Card>
              </div>
            </TabPane>
          )}

          <TabPane
            tab={
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
                <PictureOutlined style={{ color: "#faad14" }} />
                <span>Gallery</span>
              </span>
            }
            key="gallery"
          >
            <div style={{ padding: "8px 0" }}>
              {hotelDetails.HotelImages?.length > 0 ? (
                <Row gutter={[16, 16]}>
                  {hotelDetails.HotelImages.map((img, index) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <div
                        style={{
                          borderRadius: 12,
                          overflow: "hidden",
                          cursor: "pointer",
                          position: "relative",
                          transition: "all 0.3s ease",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                        onClick={() => {
                          setPreviewImage(getImageUrl(img));
                          setPreviewVisible(true);
                        }}
                      >
                        <div style={{ position: "relative", paddingBottom: "75%" }}>
                          <img
                            src={getImageUrl(img)}
                            alt={`Hotel view ${index + 1}`}
                            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
                            padding: 8,
                            textAlign: "center",
                            color: "#fff",
                            fontSize: 12,
                          }}
                        >
                          Click to enlarge
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Empty description="No images available for this hotel" />
              )}
            </div>
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="auto"
        style={{ top: 20, maxWidth: "90vw" }}
        bodyStyle={{ padding: 0, borderRadius: 12, overflow: "hidden" }}
      >
        <img
          src={previewImage}
          alt="Preview"
          style={{ width: "100%", maxHeight: "85vh", objectFit: "contain", display: "block" }}
        />
      </Modal>

      <Modal
        title={
          <Space>
            <PlusOutlined style={{ color: "#1890ff" }} />
            <span>Add New Room</span>
          </Space>
        }
        open={addRoomModalVisible}
        onCancel={() => {
          setAddRoomModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleAddRoom} style={{ marginTop: 20 }}>
          <Form.Item label="Room Type" name="type" rules={[{ required: true, message: "Please select room type!" }]}>
            <Select placeholder="Select type" size="large">
              <Option value="Single">Single</Option>
              <Option value="Double">Double</Option>
              <Option value="Twin">Twin</Option>
              <Option value="Accessible">Accessible</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Total Rooms" name="Total_rooms" rules={[{ required: true, message: "Please enter total count!" }]}>
            <InputNumber
              placeholder="e.g., 20"
              size="large"
              style={{ width: "100%" }}
              min={1}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 30 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setAddRoomModalVisible(false);
                  form.resetFields();
                }}
                size="large"
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={addRoomLoading} size="large">
                Add Room Type
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: "#1890ff" }} />
            <span>Edit Room Type — {editingRoom?.type}</span>
          </Space>
        }
        open={editRoomModalVisible}
        onCancel={() => {
          setEditRoomModalVisible(false);
          setEditingRoom(null);
          editForm.resetFields();
        }}
        footer={null}
        width={450}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateRoom} style={{ marginTop: 20 }}>
          <Form.Item label="Room Type" name="type">
            <Input size="large" disabled style={{ backgroundColor: "#f5f5f5" }} />
          </Form.Item>

          <Form.Item label="Total Rooms" name="Total_rooms" rules={[{ required: true, message: "Please enter total count!" }]}>
            <InputNumber placeholder="e.g., 25" size="large" style={{ width: "100%" }} min={1} />
          </Form.Item>

          <Form.Item label="Status" name="status" rules={[{ required: true, message: "Please select status!" }]}>
            <Select size="large">
              <Option value={1}>Active</Option>
              <Option value={0}>Inactive</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 30 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setEditRoomModalVisible(false);
                  setEditingRoom(null);
                  editForm.resetFields();
                }}
                size="large"
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={editRoomLoading} size="large">
                Update Type
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <Space>
            {editingMeal ? <EditOutlined style={{ color: "#fa8c16" }} /> : <PlusOutlined style={{ color: "#fa8c16" }} />}
            <span>{editingMeal ? "Edit Meal" : "Add New Meal"}</span>
          </Space>
        }
        open={mealModalVisible}
        onCancel={() => {
          setMealModalVisible(false);
          setEditingMeal(null);
          mealForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={mealForm} layout="vertical" onFinish={handleSaveMeal} style={{ marginTop: 20 }} initialValues={{ available: true }}>
          <Form.Item label="Meal Name" name="meal_name" rules={[{ required: true, message: "Please select meal name!" }]}>
            <Select placeholder="Select meal" size="large">
              <Option value="Breakfast">Breakfast</Option>
              <Option value="Lunch">Lunch</Option>
              <Option value="Dinner">Dinner</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Available" name="available" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 30 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setMealModalVisible(false);
                  setEditingMeal(null);
                  mealForm.resetFields();
                }}
                size="large"
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={mealSaveLoading} size="large" style={{ background: "#fa8c16", borderColor: "#fa8c16" }}>
                {editingMeal ? "Update Meal" : "Add Meal"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      {/* QR Code Modal */}
      <Modal
        title={
          <Space>
            <QrcodeOutlined />
            <span>Hotel Allocation QR</span>
          </Space>
        }
        visible={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setQrModalVisible(false)}>Close</Button>,
          <Button
            key="resend"
            type="primary"
            icon={<MailOutlined />}
            onClick={() => handleResendEmail()}
          >
            Resend Email
          </Button>
        ]}
        centered
        width={400}
      >
        {selectedAllocation && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{
              background: 'white',
              padding: 16,
              borderRadius: 12,
              display: 'inline-block',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              marginBottom: 20
            }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${WebsiteURL}/qr-register/${selectedAllocation.qr_token}`)}`}
                alt="QR Code"
                style={{ width: 200, height: 200 }}
              />
            </div>
            <Title level={5} style={{ marginBottom: 4 }}>Passenger Registration Link</Title>
            <Text copyable style={{ fontSize: 13, color: '#666' }}>
              {`${WebsiteURL}/qr-register/${selectedAllocation.qr_token}`}
            </Text>
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ textAlign: 'left' }}>
              <Row gutter={[16, 8]}>
                <Col span={12}><Text type="secondary">Status:</Text></Col>
                <Col span={12}><Badge status={selectedAllocation.status === 1 ? "success" : "default"} text={selectedAllocation.status === 1 ? "Active Allocation" : "Inactive Allocation"} /></Col>
                {/* <Col span={12}><Text type="secondary">Total Allocated Rooms:</Text></Col>
                <Col span={12}><Text strong>{selectedAllocation.available_rooms}</Text></Col> */}
                <Col span={12}><Text type="secondary">Password Protected:</Text></Col>
                <Col span={12}>
                  <Tag color={selectedAllocation.qr_password ? "green" : "default"}>
                    {selectedAllocation.qr_password ? "Yes" : "No"}
                  </Tag>
                </Col>
              </Row>
            </div>
          </div>
        )}
      </Modal>

      {/* Set Password Modal */}
      <Modal
        title={
          <Space>
            <LockOutlined />
            <span>Set Hotel Allocation Password</span>
          </Space>
        }
        visible={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        onOk={() => passwordForm.submit()}
        confirmLoading={passwordLoading}
        okText="Save Password"
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Passengers will be required to enter this password after scanning the QR code for this hotel.
          </Text>
        </div>
        <Form
          form={passwordForm}
          onFinish={handleSetPassword}
          layout="vertical"
        >
          <Form.Item
            label="New Password"

            name="password"
            rules={[
              { required: true, whitespace: true, message: "Please enter a password" },
              { min: 4, message: "Password must be at least 4 characters" }
            ]}
          >
            <Input.Password placeholder="Enter secure password" size="large" />
          </Form.Item>
        </Form>
      </Modal>

    </Card>
  );
};

export default HotelDetails;

